/**
 * Created by Jerker Nord on 2018-06-19.
 */

import {
    ISymbolStateConfig,
    ISymbolStates,
    StateReelSymbol,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite} from "gsap";
import {IceIceYetiGameConfig} from "../IceIceYetiGameConfig";

export interface IIceIceYetiYetiSymbolStates extends ISymbolStates {
    noWin:ISymbolStateConfig;
    freeze:ISymbolStateConfig;
    frozen:ISymbolStateConfig;
    land:ISymbolStateConfig;
    attentionInIce:ISymbolStateConfig;
    stickyAttention:ISymbolStateConfig;
    spinNearWin:ISymbolStateConfig;
}

export class IceIceYetiReelSymbol extends StateReelSymbol {

    private _isFrozen:boolean;
    private _isSpinNearWin:boolean;
    private _spinNearWinGold:PIXI.Sprite;
    private _spinNearWinGlow:PIXI.Sprite;
    private _spinNearWinSparkles:TimelineSprite;
    private _stickyAttention:PIXI.Sprite;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig) {
        super(symName, reelId, state);
        this._isFrozen = false;
        this._stickyAttention = new PIXI.Sprite(GameResources.getTextures(symName + "/" + symName)[0]);
        this._stickyAttention.anchor.set(0.5, 0.5);
        this._stickyAttention.alpha = 0;
    }

    get isFrozen():boolean {
        return this._isFrozen;
    }

    set isFrozen(value:boolean) {
        this._isFrozen = value;
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        const result:boolean = super.initState(stateConfig);

        if(this._spinNearWinGold && this.children.indexOf(this._spinNearWinGold) > -1) {
            this.addChild(this._spinNearWinGold)
        }
        if(this._spinNearWinGlow && this.children.indexOf(this._spinNearWinGlow) > -1) {
            this.addChild(this._spinNearWinGlow);
        }
        if(this._spinNearWinSparkles && this.children.indexOf(this._spinNearWinSparkles) > -1) {
            this.addChild(this._spinNearWinSparkles);
        }

        return result;
    }

    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);

        if(newState.state.keyword === IceIceYetiGameConfig.instance.SYMBOL_STATES.spinNearWin.keyword) {
            timeline.add(() => this.showSpinNearWinGold(), delay);
            return;
        }

        if(newState.state.keyword === IceIceYetiGameConfig.instance.SYMBOL_STATES.land.keyword && this._isSpinNearWin) {
            timeline.add([
                TweenLite.to(this._spinNearWinGlow.scale, 0.1, {y : 0.1}),
                TweenLite.to(this._spinNearWinGlow, 0.1, {alpha : 0})
            ], delay);
            timeline.add(() => { // The animation below is not part of the timeline duration, just trigger.
                this.playNearWinLandSparkles();
                TweenLite.to(this._spinNearWinGold, 0.3, {alpha : 0, onComplete : () => this.removeSpinNearWinGold()});
            }, delay + 0.2);
            return;
        }

        if(newState.state.keyword === IceIceYetiGameConfig.instance.SYMBOL_STATES.attentionInIce.keyword && this._isSpinNearWin) {
            timeline.add(() => this.removeSpinNearWinGold(), delay);
            return;
        }

        if(newState.state.keyword === IceIceYetiGameConfig.instance.SYMBOL_STATES.normal.keyword && this._isSpinNearWin) {
            timeline.add(() => this.removeSpinNearWinGold(), delay);
            return;
        }

        if(newState.state.keyword === IceIceYetiGameConfig.instance.SYMBOL_STATES.win.keyword) {
            const winTl:TimelineLite = new TimelineLite();
            winTl.add(new TweenLite(this.scale, 0.2, {x : 1.04, y : 1.04}));
            winTl.add(new TweenLite(this.scale, 0.2, {x : 1, y : 1}), 0.5);
            timeline.add(winTl, delay);
            return;
        }

        if(newState.state.keyword === IceIceYetiGameConfig.instance.SYMBOL_STATES.stickyAttention.keyword) {
            const winTl:TimelineLite = new TimelineLite();
            this.addChild(this._stickyAttention);
            winTl.add(new TweenLite(this._stickyAttention, 0.2, {alpha : 1}), "+=0.2");
            timeline.add(winTl, delay);
        }
    }

    private showSpinNearWinGold():void {
        if(!this._spinNearWinGold) {
            this._spinNearWinGold = new PIXI.Sprite(GameResources.getTextures("W_spinNearWin")[0]);
            this._spinNearWinGold.blendMode = PIXI.BLEND_MODES.SCREEN;
            this._spinNearWinGold.anchor.set(0.5, 0.5);

            this._spinNearWinGlow = new PIXI.Sprite(GameResources.getTextures("W_spinNearWinGlow")[0]);
            this._spinNearWinGlow.blendMode = PIXI.BLEND_MODES.SCREEN;
            this._spinNearWinGlow.anchor.set(0.5, 1);
            this._spinNearWinGlow.position.set(0, 57); // W_spinNearWin.height / 2
        }
        this._spinNearWinGold.alpha = 1;
        this._spinNearWinGlow.alpha = 1;
        this._spinNearWinGold.scale.y = 1;
        this.addChild(this._spinNearWinGold);
        this.addChild(this._spinNearWinGlow);
        this._isSpinNearWin = true;
    }

    private removeSpinNearWinGold():void {
        if(!this._spinNearWinGold) {
            return;
        }

        this._spinNearWinGold.alpha = 0;
        this._spinNearWinGlow.alpha = 0;
        this.removeChild(this._spinNearWinGold);
        this.removeChild(this._spinNearWinGlow);
        this._isSpinNearWin = false;
    }

    private playNearWinLandSparkles():void {
        if(!this._spinNearWinSparkles) {
            this._spinNearWinSparkles = new TimelineSprite(GameResources.getTextures("W_spinNearWinParticles"));
            this._spinNearWinSparkles.anchor.set(0.5, 1);
            this._spinNearWinSparkles.position.set(0, 57);
        }

        this._spinNearWinSparkles.getAnimation([0], undefined, () => {
            this.removeChild(this._spinNearWinSparkles);
        });
        this.addChild(this._spinNearWinSparkles);
    }

    protected reset(remove:boolean):void {
        this._isFrozen = false;
        super.reset(remove);
    }
}